import { boolean, mixed, number, object, string } from 'yup';

import { EFilterColor } from 'shared/consts/filters';
import { ECexName } from 'web3';

import { ECexOrderSide, ECexOrderStatus } from './cex/orders';

export enum EArbitragePanel {
  pairs = 'pairs',
  settings = 'settings',
  last_price = 'last_price',
  pnl = 'pnl',
  trade_history = 'trade_history',
}

export interface IPanelProps {
  initialOpened?: boolean;
  setIsOpened?: (v: boolean) => any;
}

interface IArbitragePairCommon {
  allow_rebalancer_buy: boolean;
  allow_rebalancer_sell: boolean;
  arbitrable: boolean;
  check_balance: boolean;
  pair_id: number;
  reserved_balance_base: string;
  reserved_balance_percent: number;
  reserved_balance_quote: string;
  subscribe_to_updates: boolean;
  target_base: string;
  target_base_quote_percent: number;
}

export interface IArbitragePairCex extends IArbitragePairCommon {
  account_id: number;
}

export interface IArbitragePairDex extends IArbitragePairCommon {
  wallet_id: number;
  slippage_percent: number;
}

export enum ArbitrageStableCheckingMethod {
  check_default = 'check_default',
  check_by_average_price = 'check_by_average_price',
  check_by_liquidity = 'check_by_liquidity',
  check_by_update_time = 'check_by_update_time',
}

export interface IArbitrageConfig {
  active: boolean;
  stop_reason?: string;
  default_cancel_timeout_sec: number;
  execute_cex: boolean;
  execute_dex: boolean;
  id: number;
  is_maximum_limited: boolean;
  maximum_limit_usd: number;
  min_profit_percentage: number;
  pairs_cex: IArbitragePairCex[] | null;
  pairs_dex: IArbitragePairDex[] | null;
  project_id: number;
  usd_threshold: number;
  simulation_start_at?: string;
  simulation_period?: number;
  stable_checking_method: ArbitrageStableCheckingMethod;
  close_position_automatically: boolean;
}

export interface IArbitrageGeneralSettings {
  min_profit: string;
  maximum_limit_usd: string;
  default_cancel_timeout_sec: number | null;
  is_maximum_limited: boolean;
  usd_threshold: string;
  stable_checking_method: ArbitrageStableCheckingMethod;
  close_position_automatically: boolean;
}

interface IArbitragePairSettingsCommon {
  arbitrable?: boolean;
  subscribe_to_updates?: boolean;
  allow_rebalancer_buy?: boolean;
  allow_rebalancer_sell?: boolean;
  check_balance?: boolean;
  target_base?: string;
  is_reserved_balance_base_percentage?: boolean;
  is_target_base_percentage?: boolean;
  reserved_balance_base?: string;
  reserved_balance_quote?: string;
}

export interface IArbitrageCexPairSettings extends IArbitragePairSettingsCommon {
  accountId?: number;
}

export interface IArbitrageDexPairSettings extends IArbitragePairSettingsCommon {
  walletId?: number;
  slippage_percent?: string;
}

export const generalArbitrageSettingsSchema = object({
  min_profit: string()
    .required('"Min profit" field is required')
    .test(
      'min_profit',
      '"Min profit" must be a percent value',
      (value: string | undefined) => !isNaN(Number(value)) && Number(value) <= 100,
    ),
  usd_threshold: string().required('"USD threshold" is required'),
  maximum_limit_usd: string().required('"Maximum limit (USD)" is required'),
  default_cancel_timeout_sec: number().required('"Default cancel timeout" is required'),
  is_maximum_limited: boolean(),
  stable_checking_method: mixed<ArbitrageStableCheckingMethod>()
    .oneOf(Object.values(ArbitrageStableCheckingMethod))
    .required('Stable checking method is required'),
  close_position_automatically: boolean(),
});

const pairArbitrageSettings = {
  arbitrable: boolean(),
  subscribe_to_updates: boolean(),
  allow_rebalancer_buy: boolean(),
  allow_rebalancer_sell: boolean(),
  check_balance: boolean(),
  target_base: string()
    .required('Field is required')
    .test(
      'target_base',
      'Enter valid amount',
      (value: string | undefined) => !isNaN(Number(value)) && Number(value) >= 0,
    ),
  reserved_balance_base: string()
    .required('Field is required')
    .test(
      'reserved_balance_base',
      'Enter valid amount',
      (value: string | undefined) => !isNaN(Number(value)) && Number(value) >= 0,
    ),
  reserved_balance_quote: string()
    .required('Field is required')
    .test(
      'reserved_balance_quote',
      'Enter valid amount',
      (value: string | undefined) => !isNaN(Number(value)) && Number(value) >= 0,
    ),
};

export const pairCexArbitrageSettingsSchema = object({
  ...pairArbitrageSettings,
  accountId: number()
    .required('You should select account')
    .test('accountId', 'Select only one account', (accountId: number | undefined) => {
      return accountId !== undefined && !isNaN(Number(accountId));
    }),
});

export const pairDexArbitrageSettingsSchema = object({
  ...pairArbitrageSettings,
  walletId: number()
    .required('You should select wallet')
    .test('walletId', 'Select only one wallet', (walletId: number | undefined) => {
      return walletId !== undefined && !isNaN(Number(walletId));
    }),
  slippage_percent: string()
    .required('Field is required')
    .test(
      'slippage_percent',
      'Enter valid amount',
      (value: string | undefined) => !isNaN(Number(value)) && Number(value) >= 0,
    ),
});

/**
 * @deprecated no usages in the project
 */
export interface IArbitrageInventories {
  inventories: Record<
    number,
    {
      inventory: string;
      target_base_amount: string;
      vwap: string;
    }
  >;
}

export interface IArbitrageTrade {
  id: number;
  cex_order_id: string;
  cex_trade_id: string;
  base_amount: string;
  quote_amount: string;
  base_fee: string;
  quote_fee: string;
  price: string;
  created_at: string;
}

export interface IArbitrageBalancerOrder {
  account_id: number;
  amount_usd: string;
  base_amount: string;
  base_fee: string;
  bot: 'arbitrage';
  cex: ECexName;
  cex_client_order_id: string;
  cex_order_id: string;
  created_at: string;
  deleted_at: string;
  fact_base_amount: string;
  fact_price: string;
  fact_quote_amount: string;
  id: number;
  locked_until: string;
  metadata: string;
  pair_id: number;
  price: string;
  project_id: number;
  quote_amount: string;
  quote_fee: string;
  reserved_released: boolean;
  sequence_no: number;
  side: ECexOrderSide;
  status: string;
  trades?: IArbitrageTrade[];
  updated_at: string;
}

export interface IArbitrageBalancerSwap {
  ID: number;
  CreatedAt: string;
  Identifier: string;
  AmountBase: string;
  AmountQuote: string;
  AmountFeeInUsd: string;
  Price: string;
  Status: string;
  Side: string;
  PairID: number;
}

export interface IArbitrage {
  account_id: number;
  failed_reason?: string;
  found_at: string;
  id: number;
  ineffective_at: string;
  max_available_profit: number;
  profit_expected: number;
  profit_fact: number;
  profit_token: string;
  status: 'failed' | 'executed';
  uuid: string;
  opportunities?: Maybe<
    {
      order: Nullable<IArbitrageBalancerOrder>;
      swap: Nullable<IArbitrageBalancerSwap>;
      path_element_number: number;
    }[]
  >;
}

export interface IArbitrageBalancerRecord {
  pair_token_base: string;
  pair_token_quote: string;
  amount_view_base: string;
  amount_view_quote: string;
  amount_raw_base: string;
  amount_raw_quote: string;
  amount_filled_base_view: string;
  amount_filled_quote_view: string;
  amount_filled_raw_base: string;
  amount_filled_raw_quote: string;
  price_view: string;
  price_fact_view: string;
  price_raw: string;
  price_fact_raw: string;
  status_view:
    | {
        value: ECexOrderStatus;
        label: string;
        color: EFilterColor;
      }
    | undefined;
  created_at_view: string;
  updated_at_view: string;
  failed_reason?: string;
  fee_view: string;
  fee_raw: string;
  order_tx_id: string;
  id: number;
  cex?: ECexName;
  side?: ECexOrderSide;
  trades?: IArbitrageTrade[];
  type: string;
}
